import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    input,
    OnDestroy,
    output,
    signal,
    viewChild
} from "@angular/core";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";

const debug = initDebugger('IframeAutoHoydeKomponent');

/**
 * Viser innhold i en iframe og tilpasser iframens høyde automatisk til innholdets høyde ved lasting.
 *
 * Støtter enten direkte kildeadresse via `src` eller rå HTML-innhold via `html`.
 */
@Component({
    selector: ' iframe-auto-hoyde',
    templateUrl: 'iframe-auto-hoyde.html',
    styleUrl: 'iframe-auto-hoyde.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'data-testid': 'iframe-auto-hoyde-kontainer'
    }
})
export class IframeAutoHoydeKomponent implements OnDestroy {
    static ajsSelector = 'iframeAutoHoyde';

    readonly html = input<string>('');
    readonly tittel = input<string>('Iframe-innhold');
    readonly minimumHoyde = input<number>(0);
    readonly overstyrtHoyde = input<number | string | null | undefined>(null, {alias: 'hoyde'});

    readonly lastet = output<number>();

    readonly iframeElement = viewChild<ElementRef<HTMLIFrameElement>>('iframeElement');

    readonly beregnetHoyde = signal<number | null>(null);

    private gjeldendeBlobUrl: string | null = null;
    private resizeObserver: ResizeObserver | null = null;

    readonly effektivSrc = computed<SafeResourceUrl | null>(() => {
        const htmlInnhold = this.html();
            this.ryddBlobUrl();
            if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
                const blob = new Blob([htmlInnhold], {type: 'text/html;charset=UTF-8'});
                this.gjeldendeBlobUrl = URL.createObjectURL(blob);
                return this.sanitizer.bypassSecurityTrustResourceUrl(this.gjeldendeBlobUrl);
            }
            const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(htmlInnhold)}`;
            return this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl);
    });

    readonly effektivHoyde = computed<string>(() => {
        const overstyrt = this.overstyrtHoyde();
        if (overstyrt !== null && overstyrt !== undefined) {
            return typeof overstyrt === 'number' ? `${overstyrt}px` : overstyrt;
        }

        const beregnet = this.beregnetHoyde();
        const minHoyde = this.minimumHoyde();
        if (beregnet !== null) {
            const hoyde = Math.max(beregnet, minHoyde);
            return `${hoyde}px`;
        }

        if (minHoyde > 0) {
            return `${minHoyde}px`;
        }

        return 'auto';
    });

    constructor(private sanitizer: DomSanitizer) {}

    ngOnDestroy() {
        this.ryddBlobUrl();
        this.ryddResizeObserver();
    }

    vedLasting() {
        this.oppdaterHoyde();
        this.startObserveringAvInnhold();
    }

    private oppdaterHoyde() {
        const iframe = this.iframeElement()?.nativeElement;
        if (!iframe) {
            return;
        }

        try {
            const vindu = iframe.contentWindow;
            const doc = vindu?.document || iframe.contentDocument;
            if (!doc || !doc.body) {
                return;
            }

            const bodyScrollHeight = doc.body.scrollHeight || 0;
            const docScrollHeight = doc.documentElement ? doc.documentElement.scrollHeight : 0;
            const bodyOffsetHeight = doc.body.offsetHeight || 0;
            const docOffsetHeight = doc.documentElement ? doc.documentElement.offsetHeight : 0;

            const hoyde = Math.max(bodyScrollHeight, docScrollHeight, bodyOffsetHeight, docOffsetHeight);
            if (hoyde > 0) {
                this.beregnetHoyde.set(hoyde);
                this.lastet.emit(hoyde);
            }
        } catch (feil) {
            debug('Kunne ikke lese høyde fra iframe (mulig cross-origin)', feil);
        }
    }

    private startObserveringAvInnhold() {
        this.ryddResizeObserver();
        const iframe = this.iframeElement()?.nativeElement;
        if (!iframe) {
            return;
        }

        try {
            const doc = iframe.contentWindow?.document || iframe.contentDocument;
            if (doc?.body && typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(() => {
                    this.oppdaterHoyde();
                });
                this.resizeObserver.observe(doc.body);
            }
        } catch (feil) {
            debug('Kunne ikke koble ResizeObserver til iframe', feil);
        }
    }

    private ryddBlobUrl() {
        if (this.gjeldendeBlobUrl) {
            if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
                URL.revokeObjectURL(this.gjeldendeBlobUrl);
            }
            this.gjeldendeBlobUrl = null;
        }
    }

    private ryddResizeObserver() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }
}
